import React from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { FeedbackGrid } from './FeedbackGrid'
import type { FeedbackGridProps } from './types/feedbackGrid.types'

// Create MUI theme
const theme = createTheme({
	palette: {
		mode: 'light',
		primary: {
			main: '#1976d2',
		},
		secondary: {
			main: '#dc004e',
		},
	},
	components: {
		MuiDataGrid: {
			styleOverrides: {
				root: {
					border: 'none',
				},
			},
		},
	},
})

const parseMaybeJson = <T,>(raw: string | null | undefined): T | null => {
	if (!raw) return null
	try {
		return JSON.parse(raw) as T
	} catch {
		return null
	}
}

// Track initialized elements to prevent re-creating roots
const initializedElements = new WeakSet<HTMLElement>()

const bootstrapOne = (el: HTMLElement): void => {
	// Skip if already initialized
	if (initializedElements.has(el)) {
		console.log('FeedbackGrid: Element already initialized, skipping')
		return
	}

	const endpoints = parseMaybeJson<FeedbackGridProps['endpoints']>(el.getAttribute('data-endpoints'))
	const initialData = parseMaybeJson<FeedbackGridProps['initialData']>(el.getAttribute('data-initial-data'))
	const columns = parseMaybeJson<FeedbackGridProps['columns']>(el.getAttribute('data-columns'))
	const systemColumns = parseMaybeJson<FeedbackGridProps['systemColumns']>(el.getAttribute('data-system-columns'))
	const settings = parseMaybeJson<FeedbackGridProps['settings']>(el.getAttribute('data-settings'))
	const initialFilters = parseMaybeJson<FeedbackGridProps['initialFilters']>(el.getAttribute('data-initial-filters'))
	const translations = parseMaybeJson<FeedbackGridProps['translations']>(el.getAttribute('data-translations'))
	const defaultSort = parseMaybeJson<FeedbackGridProps['defaultSort']>(el.getAttribute('data-default-sort'))
	const blockIdRaw = el.getAttribute('data-block-id')
	const blockId = blockIdRaw ? parseInt(blockIdRaw, 10) : 0
	const userCategoryManagerAttr = el.getAttribute('data-user-category-manager')
	const isUserCategoryManager = userCategoryManagerAttr === '1' || userCategoryManagerAttr === 'true'
	const isAdminAttr = el.getAttribute('data-is-admin')
	const isAdmin = isAdminAttr === '1' || isAdminAttr === 'true'

	if (!endpoints || !initialData || !columns || !systemColumns || !settings || !translations) {
		console.error('FeedbackGrid: Missing required data attributes', {
			endpoints,
			initialData,
			columns,
			systemColumns,
			settings,
		})
		return
	}

	const props: FeedbackGridProps = {
		endpoints,
		initialData,
		columns,
		systemColumns,
		settings,
		blockId,
		isUserCategoryManager,
		isAdmin,
		initialFilters: initialFilters || undefined,
		translations,
		defaultSort: defaultSort,
	}

	// Mark as initialized BEFORE creating root to prevent race conditions
	initializedElements.add(el)

	// Create root and render
	const root = createRoot(el)
	root.render(
		<React.StrictMode>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<FeedbackGrid {...props} />
			</ThemeProvider>
		</React.StrictMode>,
	)
}

export const bootstrapFeedbackGrid = (scope?: HTMLElement | Document): void => {
	const root = scope ?? document
	const elements: NodeListOf<HTMLElement> = root.querySelectorAll('.js-feedback-grid')
	elements.forEach((el) => bootstrapOne(el))
}

// Export for use in other modules
export { FeedbackGrid }
export * from './types/feedbackGrid.types'
